<template>
  <div class="row">
    <div class="col-md-8 mx-auto">
      <div class="card">
        <div class="card-header">
          <h4>{{ editingStudent ? 'Hallgató szerkesztése' : 'Belépési nyilatkozat kitöltése' }}</h4>
          <div v-if="editingStudent" class="mt-2">
            <button type="button" class="btn btn-sm btn-secondary" @click="$emit('cancelEdit')">
              Mégse
            </button>
          </div>
        </div>
        <div class="card-body">
          <form @submit.prevent="submitForm">
            <div class="row">
              <div class="col-md-6 mb-3">
                <label for="fullName" class="form-label">Teljes név *</label>
                <input 
                  type="text" 
                  class="form-control" 
                  id="fullName"
                  v-model="form.full_name" 
                  required
                >
              </div>
              <div class="col-md-6 mb-3">
                <label for="email" class="form-label">E-mail cím</label>
                <input 
                  type="email" 
                  class="form-control" 
                  id="email"
                  v-model="form.email"
                >
              </div>
            </div>

            <div class="row">
              <div class="col-md-6 mb-3">
                <label for="phone" class="form-label">Telefonszám</label>
                <input 
                  type="text" 
                  class="form-control" 
                  id="phone"
                  v-model="form.phone"
                  placeholder="06 30 123 4567"
                >
              </div>
              <div class="col-md-6 mb-3">
                <label for="neptun" class="form-label">Neptun kód</label>
                <input 
                  type="text" 
                  class="form-control" 
                  id="neptun"
                  v-model="form.neptun_code"
                  placeholder="ABC123"
                  maxlength="6"
                >
              </div>
            </div>

            <div class="row">
              <div class="col-md-6 mb-3">
                <label for="education" class="form-label">Tanulmányok</label>
                <select class="form-select" id="education" v-model="form.education_level">
                  <option value="">Válassz...</option>
                  <option value="Egyetemista">Egyetemista</option>
                  <option value="Középiskolás">Középiskolás</option>
                </select>
              </div>
              <div class="col-md-6 mb-3">
                <label for="major" class="form-label">Szakirány/tagozat</label>
                <select class="form-select" id="major" v-model="form.major">
                  <option value="">Válassz szakirányt...</option>
                  <option v-for="major in majors" :key="major.id" :value="major.name">
                    {{ major.name }}
                  </option>
                </select>
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">Nyelvkompetencia</label>
              <div v-for="(lang, index) in form.languages" :key="index" class="row mb-2">
                <div class="col-md-5">
                  <select class="form-select" v-model="lang.language">
                    <option value="">Válassz nyelvet...</option>
                    <option v-for="language in languages" :key="language.id" :value="language.name">
                      {{ language.name }}
                    </option>
                  </select>
                </div>
                <div class="col-md-4">
                  <select class="form-select" v-model="lang.level">
                    <option value="">Szint</option>
                    <option value="alap">Alap</option>
                    <option value="közép">Közép</option>
                    <option value="felsó">Felsó</option>
                  </select>
                </div>
                <div class="col-md-3">
                  <button 
                    v-if="form.languages.length > 1"
                    type="button" 
                    class="btn btn-danger btn-sm"
                    @click="removeLanguage(index)"
                  >
                    Törlés
                  </button>
                </div>
              </div>
              <button 
                type="button" 
                class="btn btn-outline-primary btn-sm"
                @click="addLanguage"
              >
                + Nyelv hozzáadása
              </button>
            </div>

            <div class="d-grid gap-2">
              <button type="submit" class="btn btn-primary btn-lg">
                {{ editingStudent ? 'Módosítások mentése' : 'Adatok mentése' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>

  <!-- Add Major Modal -->
  <div class="modal" :class="{ 'd-block': showAddMajor }" style="background: rgba(0,0,0,0.5)" v-if="showAddMajor">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Új szakirány hozzáadása</h5>
          <button type="button" class="btn-close" @click="showAddMajor = false"></button>
        </div>
        <div class="modal-body">
          <input 
            type="text" 
            class="form-control" 
            v-model="newMajor"
            placeholder="Szakirány neve"
          >
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="showAddMajor = false">Mégse</button>
          <button type="button" class="btn btn-primary" @click="addMajor">Hozzáadás</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Success message -->
  <div v-if="showSuccess" class="alert alert-success alert-dismissible fade show position-fixed" style="top: 20px; right: 20px; z-index: 1050">
    {{ successMessage }}
    <button type="button" class="btn-close" @click="showSuccess = false"></button>
  </div>
</template>

<script>
import axios from 'axios'
import API_CONFIG from '../config/api.js'

export default {
  name: 'StudentForm',
  props: {
    editingStudent: {
      type: Object,
      default: null
    },
    isAuthenticated: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      form: {
        full_name: '',
        email: '',
        phone: '',
        education_level: '',
        major: '',
        neptun_code: '',
        languages: [{ language: '', level: '' }]
      },
      majors: [],
      languages: [],
      showAddMajor: false,
      newMajor: '',
      showSuccess: false,
      successMessage: ''
    }
  },
  mounted() {
    this.loadMajors()
    this.loadLanguages()
    if (this.editingStudent) {
      this.populateForm()
    }
  },
  watch: {
    editingStudent(newVal) {
      if (newVal) {
        this.populateForm()
      } else {
        this.resetForm()
      }
    }
  },
  methods: {
    async loadMajors() {
      try {
        const response = await axios.get(API_CONFIG.BASE_URL + API_CONFIG.ENDPOINTS.MAJORS)
        this.majors = response.data
      } catch (error) {
        console.error('Error loading majors:', error)
      }
    },
    async loadLanguages() {
      try {
        const response = await axios.get(API_CONFIG.BASE_URL + API_CONFIG.ENDPOINTS.LANGUAGES)
        this.languages = response.data
      } catch (error) {
        console.error('Error loading languages:', error)
      }
    },
    async addMajor() {
      if (!this.newMajor.trim()) return
      
      try {
        const response = await axios.post(API_CONFIG.BASE_URL + API_CONFIG.ENDPOINTS.MAJORS, { name: this.newMajor })
        this.majors.push(response.data)
        this.form.major = this.newMajor
        this.newMajor = ''
        this.showAddMajor = false
      } catch (error) {
        console.error('Error adding major:', error)
      }
    },
    addLanguage() {
      this.form.languages.push({ language: '', level: '' })
    },
    removeLanguage(index) {
      if (this.form.languages.length > 1) {
        this.form.languages.splice(index, 1)
      }
    },
    populateForm() {
      if (this.editingStudent) {
        this.form = {
          full_name: this.editingStudent.full_name || '',
          email: this.editingStudent.email || '',
          phone: this.editingStudent.phone || '',
          education_level: this.editingStudent.education_level || '',
          major: this.editingStudent.major || '',
          neptun_code: this.editingStudent.neptun_code || '',
          languages: this.editingStudent.languages && this.editingStudent.languages.length > 0 
            ? this.editingStudent.languages 
            : [{ language: '', level: '' }]
        }
      }
    },
    async submitForm() {
      try {
        const formData = { ...this.form }
        formData.languages = formData.languages.filter(lang => lang.language && lang.level)
        
        if (this.editingStudent) {
          await axios.put(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.STUDENTS}/${this.editingStudent.id}`, formData, {
            withCredentials: true
          })
          this.successMessage = 'Hallgató sikeresen frissítve!'
          this.$emit('studentUpdated')
        } else {
          await axios.post(API_CONFIG.BASE_URL + API_CONFIG.ENDPOINTS.STUDENTS, formData)
          this.successMessage = this.isAuthenticated 
            ? 'Hallgató sikeresen hozzáadva! Megtekinthető a hallgatók listájában.' 
            : 'Hallgató sikeresen hozzáadva! Az admin megtekintheti a hallgatók listájában.'
          this.resetForm()
          this.$emit('studentAdded')
        }
        
        this.showSuccess = true
        setTimeout(() => { this.showSuccess = false }, 3000)
        
      } catch (error) {
        console.error('Error submitting form:', error)
        alert('Hiba történt az adatok mentése során!')
      }
    },
    resetForm() {
      this.form = {
        full_name: '',
        email: '',
        phone: '',
        education_level: '',
        major: '',
        neptun_code: '',
        languages: [{ language: '', level: '' }]
      }
    },
  }
}
</script>